import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { assetTypesApi, type AssetTypeView } from "../api/catalog";
import type { ApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { Pagination } from "../components/Pagination";
import { TYPE_CATALOG_PATHS, TypeCatalogSwitch } from "../components/TypeCatalogSwitch";
import Ic from "../components/icons.jsx";

const PAGE_SIZE = 25;
const DESCRIPTION_MAX = 90;

/**
 * `/tipovi` — one menu item for both type catalogues (2026-10-12): a user who reads only the
 * article types lands on theirs; everyone else gets the part types, with the switch when both.
 */
export default function AssetTypesRoute() {
  const { hasPermission } = useAuth();
  if (!hasPermission("types:view")) return <Navigate to={TYPE_CATALOG_PATHS.articles} replace />;
  return <AssetTypesPage />;
}

function AssetTypesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("types:create");

  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(0);

  const [items, setItems] = useState<AssetTypeView[] | null>(null);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const h = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(h);
  }, [q]);

  // A search change restarts at the first page — without an extra fetch of
  // the stale page in between.
  const prevQ = useRef(debouncedQ);

  useEffect(() => {
    if (prevQ.current !== debouncedQ) {
      prevQ.current = debouncedQ;
      if (page !== 0) {
        setPage(0);
        return;
      }
    }
    let cancelled = false;
    setError(null);
    assetTypesApi
      .list({ q: debouncedQ || undefined, page, size: PAGE_SIZE, sort: "name,asc" })
      .then((res) => {
        if (cancelled) return;
        setItems(res.content);
        setTotal(res.totalElements);
        setTotalPages(res.totalPages);
      })
      .catch((err: ApiError) => {
        if (cancelled) return;
        setError(err.message);
        setItems([]);
      });
    return () => { cancelled = true; };
  }, [debouncedQ, page]);

  const hasFilters = debouncedQ !== "";

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>{t("assetTypes.title", "Tipovi delova")}</h1>
          <div className="sub">
            {items === null
              ? t("common.loading", "Učitavanje…")
              : t("assetTypes.count", "{{count}} tipova u katalogu", { count: total })}
          </div>
        </div>
        <div className="page-actions">
          <TypeCatalogSwitch value="parts" />
          {canCreate && (
            <button type="button" className="btn primary" onClick={() => navigate("/tipovi/novi")}>
              <Ic.plus /> {t("assetTypes.new", "Novi tip")}
            </button>
          )}
        </div>
      </div>

      <div className="filter-bar">
        <div className="search-wrap">
          <Ic.search aria-hidden="true" />
          <input
            className="input"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("assetTypes.filter.searchPlaceholder", "Pretraga po šifri ili nazivu…")}
            aria-label={t("action.search", "Pretraga")}
          />
        </div>
        {hasFilters && (
          <button type="button" className="btn sm ghost" onClick={() => setQ("")}>
            {t("action.clear", "Očisti")}
          </button>
        )}
      </div>

      {error && <div style={errorBox} role="alert">{error}</div>}

      <div className="card">
        {items === null ? (
          <div className="empty">{t("common.loading", "Učitavanje…")}</div>
        ) : items.length === 0 ? (
          <div className="empty">
            {hasFilters
              ? t("assetTypes.emptyFiltered", "Nijedan tip ne odgovara pretrazi.")
              : t("assetTypes.empty", "Katalog je prazan. Dodajte prvi tip dela.")}
          </div>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl list-card">
              <thead>
                <tr>
                  <th>{t("assetTypes.col.code", "Šifra")}</th>
                  <th>{t("assetTypes.col.name", "Naziv")}</th>
                  <th className="num">{t("assetTypes.col.attributeCount", "Atributi")}</th>
                  <th>{t("assetTypes.col.description", "Opis")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr
                    key={it.id}
                    className="is-clickable"
                    onClick={() => navigate(`/tipovi/${it.id}`)}
                    onKeyDown={(e) => { if (e.key === "Enter") navigate(`/tipovi/${it.id}`); }}
                    tabIndex={0}
                  >
                    <td className="mono" data-label={t("assetTypes.col.code", "Šifra")}>{it.code}</td>
                    <td data-label={t("assetTypes.col.name", "Naziv")}><b>{it.name}</b></td>
                    <td className="num" data-label={t("assetTypes.col.attributeCount", "Atributi")}>
                      {it.attributeCount > 0 ? it.attributeCount : <span className="muted">—</span>}
                    </td>
                    <td data-label={t("assetTypes.col.description", "Opis")}>
                      {it.description
                        ? <span title={it.description.length > DESCRIPTION_MAX ? it.description : undefined}>{truncate(it.description, DESCRIPTION_MAX)}</span>
                        : <span className="muted">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <Pagination page={page} totalPages={totalPages} onChange={setPage} />
    </div>
  );
}

function truncate(text: string, max: number): string {
  const oneLine = text.replace(/\s+/g, " ").trim();
  return oneLine.length > max ? `${oneLine.slice(0, max - 1).trimEnd()}…` : oneLine;
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13, marginBottom: 12 };
